import { useEffect, useState, useRef, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  X, Bell,
} from 'lucide-react';

import SideNavigation from '../SideNavigation/SideNavigation';
import Header from '../Header/Header';
import ScreenErrorBoundary from '../ScreenErrorBoundary';
import LogUtil from '../../../utils/LogUtil';
import PosPatientModal from '../PosPatientModal';

import useOrderStore from '../../../stores/orderStore';
import useDashboardStore from '../../../stores/dashboardStore';
import useAuthStore from '../../../stores/authStore';
import useUIStore from '../../../stores/uiStore';
import { getPrescriptionService } from '../../../../domains/prescriptions';
import { getPharmDashboardService } from '../../../../domains/pharmDashboard';
import { ORDER_STATUS } from '../../../constants/orderStatus';
import styles from './MainLayout.module.css';

import { isCenteredScreen } from './centeredScreen';

const POLL_INTERVAL = 5000; // 5초
const ROWS_PER_PAGE = 20;

// 가운데 정렬 화면의 본문 배경은 흰색 — 이벤트 만들기(쿠폰·포인트)만 기존 회색 유지 (QA 10/23)
const CREATE_PATHS = ['/dashboard/coupons/distribute', '/dashboard/points/distribute'];

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pharmacy } = useAuthStore();
  const brn = pharmacy?.business_registration_number;
  const setSidebarCollapsed = useUIStore(state => state.setSidebarCollapsed);

  // posSerial은 localStorage 기반으로 uiStore에서 자동 초기화됨

  // ── 새 주문 알림 상태 ──
  const [newOrder, setNewOrder] = useState(null);
  const [approveAmount, setApproveAmount] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const orderIdsRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  const infoColRef = useRef(null);
  const [imgHeight, setImgHeight] = useState(null);
  const [imgWidth, setImgWidth] = useState(null);

  // ── QR 대기 큐 ──
  const pendingQrIdsRef = useRef([]);
  const newOrderOpenRef = useRef(false); // pollAll 클로저 안정성용

  // ── POS 새 환자 모달 상태 ──
  const [newPosPatient, setNewPosPatient] = useState(null);
  const posKeysRef = useRef(null);
  const posModalOpenRef = useRef(false); // pollAll 클로저 안정성용

  // ── POS 모달 왼쪽만 표시 (쿠폰 알림 등) ──
  const [posModalLeftOnly, setPosModalLeftOnly] = useState(false);

  // ── 환자 상세 로딩 중 폴링 스킵 ──
  const skipPollRef = useRef(false);

  // ── Store setters ──
  const setOrders = useOrderStore(state => state.setOrders);
  const setOrderLoading = useOrderStore(state => state.setLoading);
  const setVisitKpi = useDashboardStore(state => state.setVisitKpi);
  const setRxKpi = useDashboardStore(state => state.setRxKpi);
  const setPatientData = useDashboardStore(state => state.setPatientData);
  const setCouponKpi = useDashboardStore(state => state.setCouponKpi);
  const setPointKpi = useDashboardStore(state => state.setPointKpi);
  const setDashboardLoading = useDashboardStore(state => state.setLoading);

  // ── 쿠폰/포인트 1회성 로드 ──
  const couponPointLoadedRef = useRef(false);

  const loadCouponPoint = useCallback(async () => {
    if (!brn || couponPointLoadedRef.current) return;
    couponPointLoadedRef.current = true;
    try {
      const svc = getPharmDashboardService();
      const [couponRes, pointRes] = await Promise.allSettled([
        svc.getCouponSummary(brn),
        svc.getPointSummary(brn),
      ]);
      if (couponRes.status === 'fulfilled') {
        const r = couponRes.value;
        if (r?.result_code === '200' || r?.resultCode === 200) setCouponKpi(r.data);
      }
      if (pointRes.status === 'fulfilled') {
        const r = pointRes.value;
        if (r?.result_code === '200' || r?.resultCode === 200) setPointKpi(r.data);
      }
    } catch (e) {
      console.error('[MainLayout] 쿠폰/포인트 로드 실패:', e);
    }
  }, [brn, setCouponKpi, setPointKpi]);

  // ── 통합 폴링 ──
  const pollAll = useCallback(async () => {
    // 환자 상세 로딩 중이면 폴링 1회 스킵 (네트워크 양보)
    if (skipPollRef.current) {
      skipPollRef.current = false;
      return;
    }
    try {
      if (isFirstLoadRef.current) {
        setOrderLoading(true);
        setDashboardLoading(true);
      }

      const prescriptionService = getPrescriptionService();
      const dashboardService = getPharmDashboardService();

      const [orderResult, visitResult, rxResult, patientResult] = await Promise.allSettled([
        prescriptionService.getOrderList({ page: 1, size: 100 }),
        dashboardService.getVisitKpi(),
        dashboardService.getPrescriptionKpi(),
        dashboardService.getPatients(1, ROWS_PER_PAGE),
      ]);

      // ── 주문 처리 (기존 로직 유지) ──
      if (orderResult.status === 'fulfilled') {
        const response = orderResult.value;
        if (response.result_code === '200' && response.data) {
          const items = response.data.items || [];
          const currentIds = new Set(items.map(o => o.order_id));
          setOrders(items);

          // POS 시리얼 기준 필터: 이 단말로 접수된 환자(patients[].pos_serial)의 주문만 모달
          // ponytail: 환자 목록 1페이지만 대조, 일치 환자가 없으면 시리얼 미설정과 동일(전체)
          const posSerial = useUIStore.getState().posSerial;
          const patientRows =
            patientResult.status === 'fulfilled' ? patientResult.value?.data?.patients || [] : [];
          const posUserIds = new Set(
            patientRows.filter(p => posSerial && p.pos_serial === posSerial && p.user_id).map(p => p.user_id)
          );
          const isMyOrder = (order) =>
            posUserIds.size === 0 || posUserIds.has(order.user?.user_id);

          // 큐에 저장된 주문들의 QR 도착 확인
          if (pendingQrIdsRef.current.length > 0) {
            const readyIdx = pendingQrIdsRef.current.findIndex(id => {
              const order = items.find(o => o.order_id === id);
              return order?.qr_info?.qr_code && isMyOrder(order);
            });
            if (readyIdx !== -1) {
              const readyId = pendingQrIdsRef.current[readyIdx];
              const readyOrder = items.find(o => o.order_id === readyId);
              pendingQrIdsRef.current.splice(readyIdx, 1);
              if (!newOrderOpenRef.current) {
                newOrderOpenRef.current = true;
                setNewOrder(readyOrder);
              }
            }
          }

          if (orderIdsRef.current === null) {
            orderIdsRef.current = currentIds;
          } else {
            const pendingItems = items.filter(
              o => o.status === ORDER_STATUS.PENDING && !orderIdsRef.current.has(o.order_id)
            );
            if (pendingItems.length > 0) {
              for (const item of pendingItems) {
                if (!isMyOrder(item)) continue;
                if (item.qr_info?.qr_code && !newOrderOpenRef.current) {
                  newOrderOpenRef.current = true;
                  setNewOrder(item);
                } else if (!item.qr_info?.qr_code) {
                  if (!pendingQrIdsRef.current.includes(item.order_id)) {
                    pendingQrIdsRef.current.push(item.order_id);
                  }
                }
              }
            }
            orderIdsRef.current = currentIds;
          }
        }
      }

      // ── 방문 KPI ──
      if (visitResult.status === 'fulfilled') {
        const r = visitResult.value;
        if (r?.result_code === '200' || r?.resultCode === 200) setVisitKpi(r.data);
      }

      // ── 처방전 KPI ──
      if (rxResult.status === 'fulfilled') {
        const r = rxResult.value;
        if (r?.result_code === '200' || r?.resultCode === 200) setRxKpi(r.data);
      }

      // ── 환자 데이터 + POS 새 환자 감지 ──
      if (patientResult.status === 'fulfilled') {
        const r = patientResult.value;
        if (r?.result_code === '200' || r?.resultCode === 200) {
          setPatientData(r.data);

          // POS 새 환자 감지 (복합키: patient_name + visit_time)
          const patients = r.data?.patients || [];
          const posKey = (p) => `${p.patient_name}_${p.visit_time}`;

          // 감지는 모든 walk-in 대상
          const posPatients = patients.filter(p => p.visit_channel === 'walk-in');
          const currentPosKeys = new Set(posPatients.map(posKey));

          LogUtil.log('[POS 감지] walk-in 환자:', posPatients.length,
            '| 현재 Keys:', [...currentPosKeys],
            '| 이전 Keys:', posKeysRef.current ? [...posKeysRef.current] : 'null (첫 로드)');

          if (posKeysRef.current === null) {
            posKeysRef.current = currentPosKeys;
          } else {
            const newPosPatients = posPatients.filter(
              p => !posKeysRef.current.has(posKey(p))
            );
            // store의 posSerial 있으면 매칭되는 환자만 모달, 없으면 전부
            const posSerial = useUIStore.getState().posSerial;
            const modalTarget = newPosPatients.find(p =>
              posSerial ? p.pos_serial === posSerial : true
            );
            if (modalTarget && !posModalOpenRef.current) {
              LogUtil.log('[POS 감지] 새 환자 발견!', modalTarget);
              posModalOpenRef.current = true;
              setNewPosPatient(modalTarget);
            }
            posKeysRef.current = currentPosKeys;
          }
        }
      }
    } catch (error) {
      console.error('[MainLayout] 폴링 실패:', error);
    } finally {
      if (isFirstLoadRef.current) {
        setOrderLoading(false);
        setDashboardLoading(false);
        isFirstLoadRef.current = false;
      }
    }
  }, [setOrders, setOrderLoading, setVisitKpi, setRxKpi, setPatientData, setDashboardLoading]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const pollLoop = async () => {
      await pollAll();
      if (!cancelled) {
        timeoutId = setTimeout(pollLoop, POLL_INTERVAL);
      }
    };

    pollLoop();
    loadCouponPoint();

    const handleRefreshEvent = () => pollAll();
    const handleSkipPoll = () => { skipPollRef.current = true; };
    window.addEventListener('refreshOrders', handleRefreshEvent);
    window.addEventListener('patientDetailOpen', handleSkipPoll);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('refreshOrders', handleRefreshEvent);
      window.removeEventListener('patientDetailOpen', handleSkipPoll);
    };
  }, [pollAll, loadCouponPoint]);

  // ── 쿠폰 알림 모달 이벤트 리스너 ──
  useEffect(() => {
    const handleCouponModal = (e) => {
      const patient = e.detail;
      setPosModalLeftOnly(true);
      posModalOpenRef.current = true;
      setNewPosPatient(patient);
    };
    window.addEventListener('openCouponModal', handleCouponModal);
    return () => window.removeEventListener('openCouponModal', handleCouponModal);
  }, []);

  // ── 주문 모달: 이미지 크기 동기화 ──
  useEffect(() => {
    if (!newOrder || !infoColRef.current) {
      setImgHeight(null);
      setImgWidth(null);
      return;
    }
    const measure = () => {
      if (!infoColRef.current) return;
      const h = infoColRef.current.offsetHeight;
      setImgHeight(h);
      if (newOrder.prescription_image_url) {
        const img = new Image();
        img.onload = () => {
          const ratio = img.naturalWidth / img.naturalHeight;
          setImgWidth(Math.round((h - 28) * ratio));
        };
        img.src = newOrder.prescription_image_url;
      }
    };
    requestAnimationFrame(measure);
    const el = infoColRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [newOrder]);

  const orders = useOrderStore(state => state.orders);
  // ── 새 주문 핸들러 ──
  const handleNewOrderClick = () => {
    const orderId = newOrder?.order_id;
    newOrderOpenRef.current = false;
    setNewOrder(null);
    navigate('/dashboard/rx', { state: { selectedOrderId: orderId } });
  };

  const handleNewOrderClose = () => {
    newOrderOpenRef.current = false;
    setNewOrder(null);
    setApproveAmount('');
    // 큐에 QR 준비된 주문 있으면 다음 모달 오픈
    const currentOrders = useOrderStore.getState().orders;
    if (pendingQrIdsRef.current.length > 0) {
      const readyIdx = pendingQrIdsRef.current.findIndex(id => {
        const order = currentOrders.find(o => o.order_id === id);
        return order?.qr_info?.qr_code;
      });
      if (readyIdx !== -1) {
        const readyId = pendingQrIdsRef.current[readyIdx];
        const readyOrder = currentOrders.find(o => o.order_id === readyId);
        pendingQrIdsRef.current.splice(readyIdx, 1);
        newOrderOpenRef.current = true;
        setNewOrder(readyOrder);
      }
    }
  };

  const handleApproveOrder = async () => {
    if (!approveAmount || Number(approveAmount) <= 0) {
      alert('결제 금액을 입력해주세요.');
      return;
    }
    try {
      setIsApproving(true);
      const prescriptionService = getPrescriptionService();
      const response = await prescriptionService.updateOrderStatus(
        newOrder.order_id,
        { status: ORDER_STATUS.APPROVED, total_amount: Number(approveAmount) }
      );
      if (response.result_code === '200') {
        newOrderOpenRef.current = false;
        setNewOrder(null);
        setApproveAmount('');
        pollAll();
      } else {
        alert(response?.result_msg || '승인에 실패했습니다.');
      }
    } catch (error) {
      console.error('[MainLayout] 주문 승인 실패:', error);
      alert('승인에 실패했습니다.');
    } finally {
      setIsApproving(false);
    }
  };

  // ── POS 모달 핸들러 ──
  const handlePosModalClose = () => {
    posModalOpenRef.current = false;
    setNewPosPatient(null);
    setPosModalLeftOnly(false);
  };

  return (
    <div className={styles.container}>
      {/* Sidebar (240px) */}
      <SideNavigation />

      {/* Main content — main 클릭 시 사이드바 자동 접기 비활성화 (시연용) */}
      <div className={styles.main}>
        <Header />
        <main className={`${styles.content} ${isCenteredScreen(location) ? styles.contentCentered : ''} ${isCenteredScreen(location) && CREATE_PATHS.includes(location.pathname) ? styles.contentCreate : ''}`}>
          <ScreenErrorBoundary key={location.pathname}>
            <Outlet />
          </ScreenErrorBoundary>
        </main>
      </div>

      {/* ── 새 주문 알림 모달 ── */}
      {newOrder && (
        <div className={styles.modalOverlay} onClick={handleNewOrderClose}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            {/* 헤더 */}
            <div className={styles.modalHeader}>
              <div className={styles.modalIconWrap}>
                <Bell size={20} color="#0064FF" />
              </div>
              <div>
                <h3 className={styles.modalTitle}>새로운 주문</h3>
                <p className={styles.modalSub}>
                  {new Date(newOrder.created_at).toLocaleString('ko-KR', {
                    month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              </div>
              <button className={styles.modalClose} onClick={handleNewOrderClose}>
                <X size={20} />
              </button>
            </div>

            {/* 2-Column: 처방전 이미지 | 주문 정보 */}
            <div className={styles.modalRow}>
              {/* 좌: 처방전 이미지 (이미지 있을 때만 표시) */}
              {newOrder.prescription_image_url && (
                <div className={styles.modalImgCol} style={{
                  ...(imgHeight ? { height: imgHeight } : {}),
                  ...(imgWidth ? { width: imgWidth } : {}),
                }}>
                  <span className={styles.modalColLabel}>처방전</span>
                  <img
                    src={newOrder.prescription_image_url}
                    alt="처방전"
                    className={styles.modalPrescriptionImg}
                  />
                </div>
              )}

              {/* 우: 주문 정보 */}
              <div className={styles.modalInfoCol} ref={infoColRef}>
                {/* QR 코드 */}
                <div className={styles.modalQrSection}>
                  <span className={styles.modalQrLabel}>QR 코드</span>
                  <div className={styles.modalQrBox}>
                    {newOrder.qr_info?.qr_code ? (
                      <QRCodeSVG value={newOrder.qr_info.qr_code} size={120} level="M" />
                    ) : (
                      <div className={styles.modalQrPlaceholder} />
                    )}
                  </div>
                </div>

                {/* 주문 정보 카드 */}
                <div className={styles.modalCard}>
                  <div className={styles.modalInfoRow}>
                    <span className={styles.modalInfoLabel}>주문번호</span>
                    <span className={styles.modalInfoMono}>#{newOrder.order_id}</span>
                  </div>
                  <div className={styles.modalInfoDivider} />
                  <div className={styles.modalInfoRow}>
                    <span className={styles.modalInfoLabel}>환자</span>
                    <span className={styles.modalInfoValue}>{newOrder.user?.name || '-'}</span>
                  </div>
                  <div className={styles.modalInfoDivider} />
                  <div className={styles.modalInfoRow}>
                    <span className={styles.modalInfoLabel}>연락처</span>
                    <span className={styles.modalInfoValue}>{newOrder.user?.phone_number || '-'}</span>
                  </div>
                  {newOrder.total_amount > 0 && (
                    <>
                      <div className={styles.modalInfoDivider} />
                      <div className={styles.modalInfoRow}>
                        <span className={styles.modalInfoLabel}>결제금액</span>
                        <span className={styles.modalInfoAmount}>₩{newOrder.total_amount.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* 결제 금액 입력 (pending일 때만) */}
                {newOrder.status === ORDER_STATUS.PENDING && (
                  <div className={styles.modalInputSection}>
                    <label className={styles.modalInputLabel}>결제 금액 입력</label>
                    <div className={styles.modalInputWrap}>
                      <span className={styles.modalInputPrefix}>₩</span>
                      <input
                        type="number"
                        className={styles.modalInput}
                        placeholder="0"
                        value={approveAmount}
                        onChange={e => setApproveAmount(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* 버튼 */}
                <div className={styles.modalActions}>
                  {newOrder.status === ORDER_STATUS.PENDING && (
                    <button
                      className={styles.modalBtnPrimary}
                      onClick={handleApproveOrder}
                      disabled={isApproving}
                    >
                      {isApproving ? '승인 중...' : '승인하기'}
                    </button>
                  )}
                  <button className={styles.modalBtnSecondary} onClick={handleNewOrderClick}>
                    주문 상세 보기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── POS 새 환자 모달 ── */}
      {newPosPatient && (
        <PosPatientModal
          patient={newPosPatient}
          leftOnly={posModalLeftOnly}
          onClose={handlePosModalClose}
        />
      )}

    </div>
  );
};

export default MainLayout;
